import React, { useEffect, useMemo, useState } from "react";
import SolarPanelSummary from "../Common/SolarPanelSummary";
import { ThreeDots } from "react-loader-spinner";
import "./ClassFiveBudgetReport.css";

const formatCurrency = (value) => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
}).format(Number(value) || 0);

const formatPercent = (value) => `${(Number(value) || 0).toFixed(1)}%`;

const formatDate = (value) => {
  const date = value ? new Date(value) : new Date();
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const statusFor = (metric, value) => {
  if (metric === "cashFlow") {
    if (value <= -200) return { label: "Priority", tone: "danger" };
    if (value < 0) return { label: "Shortfall", tone: "danger" };
    if (value <= 200) return { label: "Limited room", tone: "warning" };
    return { label: "Positive", tone: "good" };
  }
  if (metric === "totalDTI") {
    if (value > 50) return { label: "High", tone: "danger" };
    if (value >= 36) return { label: "Review", tone: "warning" };
    return { label: "Within comparison", tone: "good" };
  }
  if (metric === "housing") {
    if (value > 35) return { label: "Above comparison", tone: "danger" };
    if (value > 30) return { label: "Watch", tone: "warning" };
    return { label: "Within comparison", tone: "good" };
  }
  if (value > 25) return { label: "High", tone: "danger" };
  if (value >= 15) return { label: "Review", tone: "warning" };
  return { label: "Within comparison", tone: "good" };
};

const ReportMetricCard = ({ label, value, note, tone = "green" }) => (
  <div className={`cc-budget-report__metric cc-budget-report__metric--${tone}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    <small>{note}</small>
  </div>
);

const ReportStatusRow = ({ title, detail, value, status }) => (
  <div className="cc-budget-report__status-row">
    <span className={`cc-budget-report__status-dot cc-budget-report__status-dot--${status.tone}`} />
    <div>
      <strong>{title}</strong>
      <small>{detail}</small>
    </div>
    <b>{value}</b>
    <span className={`cc-budget-report__status-pill cc-budget-report__status-pill--${status.tone}`}>
      {status.label}
    </span>
  </div>
);

const RatioRow = ({ label, value, target, note, tone }) => {
  const width = Math.min((Number(value) || 0) / 60 * 100, 100);
  const marker = Math.min((Number(target) || 0) / 60 * 100, 100);
  return (
    <div className="cc-budget-report__ratio-row">
      <div className="cc-budget-report__ratio-copy">
        <strong>{label}</strong>
        <small>{note}</small>
      </div>
      <div className="cc-budget-report__ratio-visual" aria-label={`${label}: ${formatPercent(value)}`}>
        <div className="cc-budget-report__ratio-track">
          <span className={`cc-budget-report__ratio-fill cc-budget-report__ratio-fill--${tone}`} style={{ width: `${width}%` }} />
          <i style={{ left: `${marker}%` }} />
        </div>
        <small>comparison marker {target}%</small>
      </div>
      <b>{formatPercent(value)}</b>
    </div>
  );
};

const printBudgetReport = () => {
  const reportNode = document.getElementById("class-five-budget-report");
  if (!reportNode) return;

  const printWindow = window.open("", "evergreen-budget-report", "width=980,height=760");
  if (!printWindow) {
    window.print();
    return;
  }

  const styles = Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style'))
    .map((node) => {
      if (node.tagName === "LINK") {
        return `<link rel="stylesheet" href="${node.href}">`;
      }
      return node.outerHTML;
    })
    .join("");

  printWindow.document.write(`<!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <title>Evergreen Budget Analysis Report</title>
        ${styles}
      </head>
      <body class="cc-budget-report-print-document">
        ${reportNode.outerHTML}
      </body>
    </html>`);
  printWindow.document.close();

  const beginPrint = () => {
    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  };

  printWindow.addEventListener("afterprint", () => printWindow.close(), { once: true });
  if (printWindow.document.readyState === "complete") beginPrint();
  else printWindow.addEventListener("load", beginPrint, { once: true });
};

const ClassFiveBudgetReport = ({
  report,
  referral,
  onReferralChange,
  referralAttempted,
  referralRef,
  onContinue,
  onBack,
  onSaveLater,
  onDashboard,
  dashboardLabel = "Go to Dashboard",
  loading,
  editorMode = false,
}) => {
  const recommendations = useMemo(() => report?.recommendations || [], [report]);
  const [selectedRecommendations, setSelectedRecommendations] = useState([]);
  const orderedRecommendations = useMemo(() => {
    const selectedIds = new Set(selectedRecommendations);
    return [...recommendations].sort(
      (a, b) => Number(selectedIds.has(b.id)) - Number(selectedIds.has(a.id))
    );
  }, [recommendations, selectedRecommendations]);

  useEffect(() => {
    setSelectedRecommendations(
      recommendations.filter((item) => item.recommended).map((item) => item.id)
    );
  }, [recommendations]);

  if (!report) {
    return (
      <div className="cc-budget-report__loading" role="status">
        <ThreeDots height="40" width="80" radius="9" color="#4e9c4b" ariaLabel="loading-budget-report" visible />
        <p>Preparing your budget analysis report...</p>
      </div>
    );
  }

  const { metrics, flags, thresholds, contact } = report;
  const cashStatus = statusFor("cashFlow", metrics.netCashFlow);
  const totalDtiStatus = statusFor("totalDTI", metrics.totalDTI);
  const housingStatus = statusFor("housing", metrics.housingPct);
  const unsecuredStatus = statusFor("unsecuredDTI", metrics.unsecuredDTI);
  const incomeContext = flags.poverty
    ? "At or below the household-size guideline"
    : flags.lowIncome
      ? "Below the report's lower comparison range"
      : flags.highIncome
        ? "Above the report's upper comparison range"
        : "Within the report's comparison range";

  const toggleRecommendation = (id) => {
    setSelectedRecommendations((current) => current.includes(id)
      ? current.filter((item) => item !== id)
      : [...current, id]);
  };

  return (
    <div className="cc-budget-report-shell">
      <div className="cc-budget-report-toolbar cc-no-print">
        <div>
          <strong>Your report is ready</strong>
          <span>Review it below, then save a PDF copy if you would like.</span>
        </div>
        <button type="button" onClick={printBudgetReport}>
          Print / Save as PDF
        </button>
      </div>

      <article className="cc-budget-report" id="class-five-budget-report">
        <header className="cc-budget-report__header">
          <div className="cc-budget-report__brand">
            <div><strong>EVERGREEN</strong><small>Financial Counseling</small></div>
          </div>
          <span>BUDGET ANALYSIS REPORT</span>
        </header>

        <div className="cc-budget-report__body">
          <div className="cc-budget-report__title-row">
            <div>
              <p>PERSONAL BUDGET REVIEW</p>
              <h1>Your budget at a glance</h1>
              <span>A clear, neutral summary of the information you entered.</span>
            </div>
            <div className="cc-budget-report__date">
              <span>Prepared for</span>
              <strong>{report.participantName}</strong>
              <small>{formatDate(report.generatedAt)}</small>
            </div>
          </div>

          <section className="cc-budget-report__cash-flow">
            <div>
              <span>ESTIMATED MONTHLY CASH FLOW</span>
              <strong>{formatCurrency(metrics.netCashFlow)}</strong>
              <p>
                {metrics.netCashFlow < 0
                  ? `Your listed expenses and minimum debt payments are about ${formatCurrency(Math.abs(metrics.netCashFlow))} higher than monthly take-home income.`
                  : `After the listed expenses and minimum debt payments, about ${formatCurrency(metrics.netCashFlow)} remains each month.`}
              </p>
            </div>
            <b className={`cc-budget-report__hero-pill cc-budget-report__hero-pill--${cashStatus.tone}`}>
              {cashStatus.label}
            </b>
          </section>

          <section className="cc-budget-report__metric-grid" aria-label="Budget totals">
            <ReportMetricCard label="Take-home income" value={formatCurrency(metrics.monthlyNet)} note="per month" />
            <ReportMetricCard label="Living expenses" value={formatCurrency(metrics.livingExpenses)} note="includes housing" tone="blue" />
            <ReportMetricCard label="Minimum debt" value={formatCurrency(metrics.minimumDebtPayments)} note="required monthly payments" tone="amber" />
            <ReportMetricCard label="Gross income" value={formatCurrency(metrics.monthlyGross)} note="before taxes, per month" tone="slate" />
          </section>

          <section className="cc-budget-report__section cc-budget-report__print-page">
            <div className="cc-budget-report__section-heading">
              <span>1</span>
              <div><h2>What your numbers suggest</h2><p>These are observations, not recommendations or judgments.</p></div>
            </div>
            <div className="cc-budget-report__status-list">
              <ReportStatusRow
                title="Monthly cash flow"
                detail={`${formatCurrency(metrics.monthlyNet)} - (${formatCurrency(metrics.livingExpenses)} + ${formatCurrency(metrics.minimumDebtPayments)})`}
                value={formatCurrency(metrics.netCashFlow)}
                status={cashStatus}
              />
              <ReportStatusRow
                title="Total debt-to-income"
                detail="Housing and debt payments as a share of gross income"
                value={formatPercent(metrics.totalDTI)}
                status={totalDtiStatus}
              />
              <ReportStatusRow
                title="Housing share"
                detail="Housing costs as a share of gross monthly income"
                value={formatPercent(metrics.housingPct)}
                status={housingStatus}
              />
              <ReportStatusRow
                title="Unsecured debt-to-income"
                detail="Unsecured debt payments as a share of gross income"
                value={formatPercent(metrics.unsecuredDTI)}
                status={unsecuredStatus}
              />
            </div>
          </section>

          <section className="cc-budget-report__section">
            <div className="cc-budget-report__section-heading">
              <span>2</span>
              <div><h2>Income, debt, and housing context</h2><p>Comparison points used by the report rules in your course.</p></div>
            </div>
            <div className="cc-budget-report__context-grid">
              <div><span>Annual gross income</span><strong>{formatCurrency(metrics.annualGross)}</strong></div>
              <div><span>Household size</span><strong>{report.householdSize}</strong></div>
              <div><span>Income context</span><strong>{incomeContext}</strong></div>
              <div><span>Household-size guideline</span><strong>{formatCurrency(thresholds.povertyMonthly)} / month</strong></div>
            </div>
            <div className="cc-budget-report__ratios">
              <RatioRow label="Total DTI" value={metrics.totalDTI} target={thresholds.totalDTI} note="Housing plus monthly debt payments" tone={totalDtiStatus.tone} />
              <RatioRow label="Unsecured DTI" value={metrics.unsecuredDTI} target={15} note="Unsecured monthly debt payments" tone={unsecuredStatus.tone} />
              <RatioRow label="Housing share" value={metrics.housingPct} target={thresholds.housingPct} note="Monthly housing costs" tone={housingStatus.tone} />
            </div>
            <p className="cc-budget-report__source-note">
              Income context uses the <a href={thresholds.sourceUrl} target="_blank" rel="noreferrer">{thresholds.sourceLabel}</a> for household-size context only. It does not determine eligibility for any program.
            </p>
          </section>

          <section className="cc-budget-report__section cc-budget-report__recommendations cc-budget-report__print-page">
            <div className="cc-budget-report__section-heading">
              <span>3</span>
              <div>
                <h2>Suggestions to consider</h2>
                <p>Some boxes may be automatically checked based on your answers. You can uncheck anything that does not fit, and you can check anything that feels useful.</p>
              </div>
            </div>
            <div className="cc-budget-report__recommendation-list">
              {orderedRecommendations.map((item) => {
                const checked = selectedRecommendations.includes(item.id);
                return (
                  <label className={`cc-budget-report__recommendation${checked ? " is-selected" : ""}`} key={item.id}>
                    <input type="checkbox" checked={checked} onChange={() => toggleRecommendation(item.id)} />
                    <span className="cc-budget-report__check" aria-hidden="true">{checked ? "✓" : ""}</span>
                    <span className="cc-budget-report__recommendation-copy">
                      <strong>{item.title}</strong>
                      <span>{item.summary}</span>
                      {item.recommended && <small><b>Why this box is checked:</b> {item.reason}</small>}
                    </span>
                    {item.recommended && <em>Suggested</em>}
                  </label>
                );
              })}
            </div>
          </section>

          <SolarPanelSummary answers={report.solarPanels} />
          {(flags.legal || report.legalActions?.length > 0) && (
            <section className="cc-budget-report__legal-note">
              <strong>Legal or collection activity was reported</strong>
              <p>A licensed attorney can explain your legal rights and options. Your counselor can provide education but cannot give legal advice.</p>
            </section>
          )}

          {!editorMode && (
            <fieldset
              ref={referralRef}
              className={`cc-budget-report__referral cc-no-print${referralAttempted && !referral ? " has-error" : ""}`}
            >
              <legend>Would you like help finding a neutral attorney referral service in your area? <span>*</span></legend>
              <label><input type="radio" name="report-attorney-referral" value="yes" checked={referral === "yes"} onChange={(event) => onReferralChange(event.target.value)} /> Yes, please connect me with a neutral attorney referral service.</label>
              <label><input type="radio" name="report-attorney-referral" value="no" checked={referral === "no"} onChange={(event) => onReferralChange(event.target.value)} /> I do not need a referral.</label>
              {referralAttempted && !referral && <p role="alert">Please select an option before continuing.</p>}
            </fieldset>
          )}

          <section className="cc-budget-report__disclaimer">
            <strong>Important note</strong>
            <p>{report.disclaimer}</p>
          </section>
        </div>

        <footer className="cc-budget-report__footer">
          <span>Evergreen Financial Counseling</span>
          <span>{contact.phone} | {contact.email}</span>
        </footer>
      </article>

      <div className="cc-budget-report-actions cc-no-print">
        {editorMode ? (
          <>
            <button type="button" className="btn_brnd cc-action-back" onClick={onBack} disabled={loading}>
              Back to Your Assets
            </button>
            {onDashboard && (
              <button
                type="button"
                className="btn_brnd cc-budget-report-actions__continue cc-budget-report-actions__dashboard"
                onClick={onDashboard}
                disabled={loading}
              >
                {dashboardLabel}
              </button>
            )}
          </>
        ) : (
          <>
            <button type="button" className="btn_brnd cc-action-back" onClick={onBack} disabled={loading}>Go Back</button>
            <button type="button" className="btn_brnd cc-action-save-later" onClick={onSaveLater} disabled={loading}>Save and Return Later</button>
            <button type="button" className="btn_brnd btn-next cc-budget-report-actions__continue" onClick={() => onContinue(selectedRecommendations)} disabled={loading}>
              {loading ? <ThreeDots height="20" width="60" radius="9" color="#fff" ariaLabel="saving-report" visible /> : "Continue Course"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ClassFiveBudgetReport;
